import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { getAllCategoriesForServiceProvider } from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Full name is required')
    .max(50, 'Full name should be at most 50 characters'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Invalid phone number'),
  email: yup.string().required('Email is required').email('Invalid email format'),
  category_id: yup.number().required('Category is required'),
  type_id: yup.string().required('Type is required')
});

const EditServiceProvider = ({ serviceProvider, onClose, onSuccess }) => {
  const userId = Cookies.get('userId');
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: serviceProvider?.user?.name || '',
      phone_number: serviceProvider?.user?.phone_number || '',
      email: serviceProvider?.user?.email || '',
      category_id: serviceProvider?.category?.id || '',
      type_id: serviceProvider?.type || ''
    }
  });

  const [categoriesForDropdown, setCategoriesForDropdown] = useState([]);

  const { data: allCategories } = useQuery({
    queryKey: ['getAllCategoriesForServiceProvider'],
    queryFn: getAllCategoriesForServiceProvider
  });

  
  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Service Provider Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (Array.isArray(allCategories)) {
      setCategoriesForDropdown(allCategories);
    }
  }, [allCategories]);

  const onSubmitForm = (data) => {
    const formattedData = {
      phone_number: data.phone_number,
      email: data.email,
      name: data.name,
      category_id: data.category_id,
      type: data.type_id
    };
    onSuccess(formattedData);
  };

  const handlePhoneNumber = useCallback(
    (val) => {
      const newVal = val.replace(/[^0-9]/g, '');
      setValue('phone_number', newVal);
    },
    [setValue]
  );

  const handleName = useCallback(
    (val) => {
      const newVal = val.replace(/[^a-zA-Z\s]/g, '');
      setValue('name', newVal);
    },
    [setValue]
  );

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Edit Service Provider Details
        </span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={100}
            label={'Full Name'}
            isRequired={true}
            register_key={'name'}
            register={register}
            type={'text'}
            errors={errors.name}
            onType={handleName}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Phone Number'}
            register={register}
            isRequired={true}
            register_key={'phone_number'}
            type={'text'}
            maxLength={10}
            errors={errors.phone_number}
            onType={handlePhoneNumber}
          />
          <FormInput
            width={48}
            label={'Email Address'}
            register={register}
            isRequired={true}
            register_key={'email'}
            type={'text'}
            errors={errors.email}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Service Provider Category'}
            register={register}
            isRequired={true}
            register_key={'category_id'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Category', disabled: true },
              ...categoriesForDropdown.map((item) => ({
                value: item.id,
                label: item.name.trim()
              }))
            ]}
            errors={errors.category_id}
          />
          <FormInput
            width={48}
            label={'Service Provider Type'}
            register={register}
            isRequired={true}
            register_key={'type_id'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Type' },
              { value: 'individual', label: 'Individual' },
              { value: 'company', label: 'Company' }
            ]}
            errors={errors.type_id}
          />
        </div>

        <div className="flex justify-end items-center space-x-4 mt-6">
          <Button type="button" displayType={'secondary'} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" displayType={'primary'}>
            Save Changes
          </Button>
        </div>
      </form>
    </>
  );
};

export default EditServiceProvider; 